import { pdf } from "@react-pdf/renderer";
import PosMenuPDF from "../components/pdf/PosMenuPDF";

const fallbackAccent = "#8b5bff";
const nativeCategoryOrder = ["Beers", "Softs", "Food", "Cocktails", "Wine", "Hot drinks", "Other"];

const sanitizeFilename = (value) =>
  String(value || "pos")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0"))
    .join("")}`;

const getDominantColorFromLogo = (logoUrl) =>
  new Promise((resolve) => {
    if (!logoUrl) {
      resolve({ accentColor: fallbackAccent, logoSrc: null });
      return;
    }

    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const size = 80;
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        context.drawImage(image, 0, 0, size, size);

        const { data } = context.getImageData(0, 0, size, size);
        const buckets = new Map();
        const bucketSize = 32;

        for (let index = 0; index < data.length; index += 4 * 4) {
          const alpha = data[index + 3];
          if (alpha < 160) continue;

          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];
          const lightness = (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
          const saturation = Math.max(r, g, b) - Math.min(r, g, b);

          if (lightness > 235 || lightness < 25 || saturation < 24) continue;

          const key = [
            Math.round(r / bucketSize),
            Math.round(g / bucketSize),
            Math.round(b / bucketSize),
          ].join("_");
          const bucket = buckets.get(key) || { count: 0, r: 0, g: 0, b: 0 };
          bucket.count += 1;
          bucket.r += r;
          bucket.g += g;
          bucket.b += b;
          buckets.set(key, bucket);
        }

        const dominant = [...buckets.values()].sort((a, b) => b.count - a.count)[0];
        const accentColor = dominant
          ? rgbToHex({
              r: dominant.r / dominant.count,
              g: dominant.g / dominant.count,
              b: dominant.b / dominant.count,
            })
          : fallbackAccent;

        resolve({ accentColor, logoSrc: canvas.toDataURL("image/png") });
      } catch (error) {
        resolve({ accentColor: fallbackAccent, logoSrc: null });
      }
    };
    image.onerror = () => resolve({ accentColor: fallbackAccent, logoSrc: null });
    image.src = logoUrl;
  });

const sortProducts = (products) =>
  [...products].sort((a, b) => {
    const orderDiff = Number(a.productOrder ?? Number.MAX_SAFE_INTEGER) - Number(b.productOrder ?? Number.MAX_SAFE_INTEGER);
    if (orderDiff !== 0) return orderDiff;
    const priceDiff = Number(a.price || 0) - Number(b.price || 0);
    if (priceDiff !== 0) return priceDiff;
    return String(a.title || "").localeCompare(String(b.title || ""));
  });

const buildMenuGroups = ({ products, customCategories, categoryLabels, uncategorizedLabel }) => {
  const visibleProducts = (products || []).filter((product) => !product.outOfStock);
  const categoryOrder = [
    ...nativeCategoryOrder,
    ...(customCategories || []).map((category) => category.title).filter(Boolean),
  ];
  const orderMap = new Map(categoryOrder.map((category, index) => [category, index]));
  const grouped = new Map();

  visibleProducts.forEach((product) => {
    const category = product.category || "Other";
    const key = orderMap.has(category) ? category : product.category || "__uncategorized";
    grouped.set(key, [...(grouped.get(key) || []), product]);
  });

  return [...grouped.entries()]
    .sort(([left], [right]) => {
      const leftOrder = orderMap.has(left) ? orderMap.get(left) : Number.MAX_SAFE_INTEGER;
      const rightOrder = orderMap.has(right) ? orderMap.get(right) : Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return left.localeCompare(right);
    })
    .map(([category, items]) => ({
      category,
      label: category === "__uncategorized" ? uncategorizedLabel : categoryLabels[category] || category,
      products: sortProducts(items),
    }));
};

export default async function downloadPosMenuPDF({
  organization,
  products,
  customCategories,
  categoryLabels,
  labels,
  locale,
}) {
  const { accentColor, logoSrc } = await getDominantColorFromLogo(organization?.logo);
  const groups = buildMenuGroups({
    products,
    customCategories,
    categoryLabels,
    uncategorizedLabel: labels.uncategorized,
  });
  const generatedAt = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());

  const blob = await pdf(
    <PosMenuPDF
      organization={organization}
      groups={groups}
      accentColor={accentColor}
      logoSrc={logoSrc}
      generatedAt={generatedAt}
      labels={labels}
      locale={locale}
    />,
  ).toBlob();

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `menu-${sanitizeFilename(organization?.organization)}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
